// Zentrale Koordination der UNTEREN Leisten-Zone — bislang gab es dafür keine
// gemeinsame Konstante, alle Leisten lagen unkoordiniert auf `bottom: 0` und
// verdeckten sich gegenseitig (Dock minimierter Fenster über der Speicher-Leiste
// etc.). Diese Datei ist bewusst React-frei, damit die Logik im Vitest-„node"-
// Runner ohne DOM getestet werden kann.

/**
 * Name der geteilten CSS-Variable, die die aktuelle Höhe des Fenster-Docks
 * (minimierte Fenster) trägt. Wird von WindowTaskbar auf `document.documentElement`
 * gesetzt und von jeder unteren Leiste konsumiert, damit sie sich ÜBER das Dock
 * staffelt statt darunter zu verschwinden. Fallback `0px`, wenn kein Dock aktiv
 * ist → identisches Verhalten wie zuvor auf Seiten ohne Fenster-Layer.
 */
export const WM_DOCK_VAR = "--wm-dock-h";

/**
 * Name der geteilten CSS-Variable, die die aktuelle Höhe der mobilen
 * Bottom-Navigation (nur Phone ≤768) trägt. Wird von `MobileBottomNav` auf
 * `document.documentElement` gesetzt (per ResizeObserver, inkl.
 * `safe-area-inset-bottom`) und beim Unmount ENTFERNT — Fallback `0px` auf
 * Tablet/Desktop, wo keine Bottom-Leiste existiert. Alle unteren Leisten +
 * das `<main>`-Padding staffeln sich über die Leiste (A6 „überschneidungsfrei").
 */
export const MOBILE_NAV_VAR = "--mobile-nav-h";

/**
 * Höhe, um die das Plus der Vorgangsansicht auf dem Handy die rechte untere
 * Ecke belegt (Plan „Vorgangsdetail mobil", PR 3). Gesetzt nur, solange das
 * Plus steht; der Melde-Knopf rückt um diesen Betrag darüber. Fallback `0px`
 * — überall sonst unverändert.
 */
export const PLUS_FAB_VAR = "--plus-fab-h";

/** Default-Abstand der CallWidget-Karte vom unteren Rand (`.callw-root`). */
export const CALL_WIDGET_BASE_BOTTOM = 24;

/**
 * z-index-Landkarte der unteren Zone (bislang verstreute Magic Numbers). Die
 * Werte spiegeln die bestehende, funktionierende Stapelung wider und dienen als
 * dokumentierte Referenz — die eigentliche Entkopplung passiert über die
 * `bottom`-Offsets (computeBottomStack), nicht über z-index, weil die Leisten in
 * verschiedenen Stacking-Kontexten leben (Dock im WindowLayer z9000, CallWidget
 * als body-Portal, Speicher-Leiste im <main>).
 */
export const BOTTOM_ZONE_Z = {
  stickyBar: 40, // .cd-savebar (im <main>-Stacking-Kontext)
  bottomBar: 50, // Risikoanalyse-BottomBar
  windowLayer: 9000, // WindowLayer-Overlay
  dock: 99999, // WindowTaskbar innerhalb des WindowLayer
  callOverlay: 1000000, // CallWidget / ActiveCallBar (body-Portale)
} as const;

export interface BottomStackInput {
  /** Anzahl minimierter Fenster im Dock (0 = kein Dock). */
  minimizedCount: number;
  /** gemessene Dock-Höhe in px (0, solange nicht gemessen/sichtbar). */
  dockHeight: number;
  /** Basis-Abstand der CallWidget-Karte (Default 24). */
  callWidgetBaseBottom?: number;
  /**
   * Höhe der mobilen Bottom-Navigation in px (0 = keine Leiste, also
   * Tablet/Desktop). Alle unteren Offsets staffeln sich zusätzlich um diesen
   * Betrag ÜBER die Leiste, sodass keine Leiste sie verdeckt (A6).
   */
  mobileNavHeight?: number;
}

export interface BottomStack {
  /** Ist das Dock aktuell sichtbar und belegt den unteren Streifen? */
  dockVisible: boolean;
  /** px-Wert für `--wm-dock-h` (0, wenn kein Dock) — reine Dock-Höhe. */
  dockOffset: number;
  /** Höhe der mobilen Bottom-Leiste (geklemmt, ganze px; 0 ohne Leiste). */
  mobileNavHeight: number;
  /** `bottom` des Fenster-Docks selbst — sitzt ÜBER der mobilen Leiste. */
  dockBottom: number;
  /** `bottom` für sticky Seiten-Leisten (Speicher-Leiste, BottomBar). */
  stickyBarBottom: number;
  /** `bottom` für das schwebende CallWidget (Basis + Dock + mobile Leiste). */
  callWidgetBottom: number;
}

/**
 * Berechnet, wie viel Platz das Dock unten belegt, und daraus die `bottom`-
 * Offsets der übrigen Leisten, sodass jede Leiste „eigenen Fokus" bekommt
 * (getrennte, gestaffelte Zone statt Überlappung).
 *
 * - Kein/leeres Dock → alle Offsets fallen auf den Ausgangszustand zurück.
 * - Sichtbares Dock → Seiten-Leisten sitzen exakt um die Dock-Höhe höher, das
 *   CallWidget zusätzlich um seinen Basis-Abstand.
 */
export function computeBottomStack(i: BottomStackInput): BottomStack {
  const base = i.callWidgetBaseBottom ?? CALL_WIDGET_BASE_BOTTOM;
  const dockVisible = i.minimizedCount > 0 && i.dockHeight > 0;
  const dockOffset = dockVisible ? Math.max(0, Math.round(i.dockHeight)) : 0;
  // Mobile Bottom-Leiste (nur Phone): negative/NaN-Werte klemmen, auf ganze px
  // runden. 0 (Default) → identisches Desktop-Verhalten wie zuvor.
  const rawMobile = i.mobileNavHeight ?? 0;
  const mobileNavHeight = Number.isFinite(rawMobile) ? Math.max(0, Math.round(rawMobile)) : 0;
  return {
    dockVisible,
    dockOffset,
    mobileNavHeight,
    // Das Dock sitzt ÜBER der mobilen Leiste, die übrigen Leisten zusätzlich
    // über dem Dock → monoton gestaffelte, überschneidungsfreie Zone (A6).
    dockBottom: mobileNavHeight,
    stickyBarBottom: dockOffset + mobileNavHeight,
    callWidgetBottom: base + dockOffset + mobileNavHeight,
  };
}

// ---------------------------------------------------------------------------
// Überlappungs-Contract (A6) — formale Absicherung, dass sich die untere
// Leisten-Zone NIE mit der mobilen Bottom-Navigation überschneidet.
//
// `computeBottomStack` liefert nur die `bottom`-OFFSETS. Um „kein Intervall-
// Overlap" beweisen zu können, brauchen wir zusätzlich die HÖHEN der beteiligten
// Elemente und daraus die belegten vertikalen Intervalle [bottom, top] (gemessen
// vom Viewport-Boden nach oben). Vollbreite, fix gestapelte Zonen (mobile Leiste,
// Fenster-Dock, sticky Seiten-Leiste) dürfen sich NICHT überschneiden — nur
// berühren ist erlaubt. Rechts schwebende Elemente (CallWidget, Kalender-FAB)
// sind horizontal versetzt und müssen lediglich ÜBER der Leiste liegen
// (`bottom ≥ Leistenhöhe`). Alles rein & DOM-frei → im Vitest-„node"-Runner
// testbar; die DEV-Runtime-Assertion der Mobile-Leisten nutzt dieselben Prädikate.
// ---------------------------------------------------------------------------

/** Default-Basisabstand des Kalender-FAB vom Content-Boden (calendar.css). */
export const FAB_BASE_BOTTOM = 16;

/** Gemessene Höhen der beteiligten unteren Elemente (px) für die Intervalle. */
export interface BottomZoneHeights {
  /** Höhe einer sticky Seiten-Leiste (Speicher-Leiste / Risikoanalyse-BottomBar). */
  stickyBarHeight?: number;
  /** Höhe der schwebenden CallWidget-Karte. */
  callWidgetHeight?: number;
  /** Kantenlänge des Kalender-FAB. */
  fabSize?: number;
  /** Basis-Abstand des FAB vom Content-Boden (Default FAB_BASE_BOTTOM). */
  fabBaseBottom?: number;
}

/** Ein vertikal belegtes Intervall, gemessen vom Viewport-Boden nach oben. */
export interface BottomZoneInterval {
  name: string;
  /** Unterkante über dem Viewport-Boden (px). */
  bottom: number;
  /** Oberkante über dem Viewport-Boden (px). */
  top: number;
  /**
   * true = spannt die volle Breite (Leiste, Dock, sticky Leiste) und darf sich
   * mit keiner anderen full-width-Zone überschneiden. false = horizontal versetzt
   * (rechts schwebend: CallWidget/FAB) → nur „nicht hinter der Leiste".
   */
  fullWidth: boolean;
}

/**
 * `<main>`-padding-bottom laut CSS
 * `calc(var(--wm-dock-h, 4px) + var(--mobile-nav-h, 0px))`:
 * Dock sichtbar → reine Dock-Höhe, sonst Fallback (4px); plus mobile Leiste.
 * Modelliert die für „Content nicht hinter der Leiste" reservierte Zone.
 */
export function mainPaddingBottom(stack: BottomStack, mainDockFallback = 4): number {
  const dockPart = stack.dockVisible ? stack.dockOffset : mainDockFallback;
  return dockPart + stack.mobileNavHeight;
}

/**
 * Belegte vertikale Intervalle der unteren Zone aus dem berechneten Stack + den
 * gemessenen Höhen. Das Dock modelliert seine Höhe als `dockOffset` (== der
 * koordinierte `--wm-dock-h`-Wert, den die sticky Leiste in ihrem `bottom`-calc
 * verwendet) — dadurch berühren sich Dock-Oberkante und sticky-Unterkante exakt.
 */
export function bottomZoneIntervals(
  stack: BottomStack,
  heights: BottomZoneHeights = {},
): BottomZoneInterval[] {
  const zones: BottomZoneInterval[] = [];
  const navH = stack.mobileNavHeight;

  if (navH > 0) {
    zones.push({ name: "mobileNav", bottom: 0, top: navH, fullWidth: true });
  }
  if (stack.dockVisible && stack.dockOffset > 0) {
    zones.push({
      name: "dock",
      bottom: stack.dockBottom,
      top: stack.dockBottom + stack.dockOffset,
      fullWidth: true,
    });
  }
  if (heights.stickyBarHeight && heights.stickyBarHeight > 0) {
    zones.push({
      name: "stickyBar",
      bottom: stack.stickyBarBottom,
      top: stack.stickyBarBottom + heights.stickyBarHeight,
      fullWidth: true,
    });
  }
  if (heights.callWidgetHeight && heights.callWidgetHeight > 0) {
    zones.push({
      name: "callWidget",
      bottom: stack.callWidgetBottom,
      top: stack.callWidgetBottom + heights.callWidgetHeight,
      fullWidth: false,
    });
  }
  if (heights.fabSize && heights.fabSize > 0) {
    const fabBottom = (heights.fabBaseBottom ?? FAB_BASE_BOTTOM) + navH;
    zones.push({
      name: "fab",
      bottom: fabBottom,
      top: fabBottom + heights.fabSize,
      fullWidth: false,
    });
  }
  return zones;
}

/** Zwei Intervalle überschneiden sich, wenn sie sich mehr als nur berühren. */
export function intervalsOverlap(a: BottomZoneInterval, b: BottomZoneInterval): boolean {
  return a.bottom < b.top && b.bottom < a.top;
}

/**
 * Alle Paare **full-width**-Zonen, die sich überschneiden (A6-Verletzung). Leer
 * = keine Überlappung. Schwebende (rechts versetzte) Zonen werden hier bewusst
 * NICHT gegeneinander geprüft — sie können horizontal koexistieren.
 */
export function detectBottomOverlaps(zones: BottomZoneInterval[]): [string, string][] {
  const full = zones.filter((z) => z.fullWidth);
  const pairs: [string, string][] = [];
  for (let i = 0; i < full.length; i++) {
    for (let j = i + 1; j < full.length; j++) {
      if (intervalsOverlap(full[i], full[j])) pairs.push([full[i].name, full[j].name]);
    }
  }
  return pairs;
}

/**
 * Höhe, die der Platzhalter der VOLLSEITEN-Leiste im Fluss freihalten muss.
 * Die Leiste ist dort absolut positioniert (volle Breite am unteren Rand der
 * Fenster-Schale) und belegt selbst keinen Platz mehr — ohne Platzhalter
 * verschwände der letzte Inhalt dahinter. Zusätzlich zur gemessenen
 * Leistenhöhe kommt die laufend aktualisierte Dock-Höhe als CSS-Variable
 * hinzu, weil die Leiste um genau diesen Betrag höher sitzt.
 */
export function pageFullBarSpacerHeight(barHeight: number): string {
  const h = Math.max(0, Math.round(barHeight));
  return `calc(${h}px + var(${WM_DOCK_VAR}, 0px))`;
}

/**
 * Löst `pageFullBarSpacerHeight` gegen einen konkreten Dock-Offset auf — für
 * Tests und DEV-Assertions, die mit px rechnen statt mit CSS-Variablen.
 */
export function resolveSpacerHeight(css: string, dockOffset: number): number {
  const m = /^calc\((\d+)px \+ var\(/.exec(css);
  if (!m) return NaN;
  return Number(m[1]) + dockOffset;
}

/**
 * DEV-Assertion-Helfer: true, wenn das gemessene `<main>`-Padding kleiner als die
 * Leistenhöhe ist — dann würde der letzte Content hinter der fixen Leiste
 * verschwinden (A6-Bruch). Wird von den Mobile-Leisten hinter `import.meta.env.DEV`
 * als Runtime-Warnung genutzt.
 */
export function contentHiddenBehindBar(barHeight: number, mainPaddingBottomPx: number): boolean {
  return barHeight > 0 && mainPaddingBottomPx < barHeight;
}
